import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";
import { useAppContext } from "../useAppContext";
import { useClient } from "../useClient";

const USER_DATES_KEY = ["use-user-events"];

export function useUserEvents() {
	const { reactQueryNoReloadOptions } = useAppContext();

	const client = useClient();

	return useQuery<eXpress.core.IUserDate[], unknown, Required<eXpress.core.IUserDate>[]>({
		queryKey: USER_DATES_KEY,
		queryFn: () =>
			client.getUserDatesAsync().catch((err: unknown) => {
				// useQuery will rerun queries that returned an error at each render
				// To prevent this we return an empty array if the user is not authenticated
				// Otherwise it takes some time before results are in.
				if (err instanceof eXpress.api.NotAuthenticatedException) {
					return [];
				}
				throw err;
			}),
		select: (dates) =>
			eXpress.creator.calendars.calendarUtils.sortUserDates(
				dates,
				eXpress.creator.calendars.calendarUtils.sortUserDatesByName,
			),
		...reactQueryNoReloadOptions,
		retry: false, // Already in SmartClient, otherwise multiple
	});
}

export function useUserEventsActions() {
	const queryClient = useQueryClient();

	const client = useClient();
	const addUserDatesAsync = useMutation({
		mutationFn: (userDates: eXpress.core.IUserDate[]) => client.addUserDatesAsync(userDates),

		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: USER_DATES_KEY });
		},
	}).mutateAsync;

	const deleteUserDatesAsync = useMutation({
		mutationFn: (ids: eXpress.GuidString[]) => client.deleteUserDatesAsync(ids),
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: USER_DATES_KEY });
		},
	}).mutateAsync;

	const updateUserDatesAsync = useMutation({
		mutationFn: (userDates: Required<eXpress.core.IUserDate>[]) =>
			client.updateUserDatesAsync(userDates),
		onSuccess: () => {
			return queryClient.invalidateQueries({ queryKey: USER_DATES_KEY });
		},
	}).mutateAsync;

	const invalidateCacheAsync = useCallback(() => {
		return queryClient.invalidateQueries({ queryKey: USER_DATES_KEY });
	}, [queryClient]);

	return useMemo(() => {
		return {
			addUserDatesAsync,
			deleteUserDatesAsync,
			invalidateCacheAsync,
			updateUserDatesAsync,
		};
	}, [addUserDatesAsync, deleteUserDatesAsync, invalidateCacheAsync, updateUserDatesAsync]);
}
